import { employees } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { attachmentResponseSchema, withAttachments } from './attachment'
import { selectEmployeeSkillWithDetailsSchema } from './skill'

export const selectEmployeeSchema = createSelectSchema(employees)

export const insertEmployeeSchema = createInsertSchema(employees).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  userId: true, // Will be auto-created
})

export const updateEmployeeSchema = z.object({
  // Allow updating user fields - requires id for safety
  user: z
    .object({
      id: z.number(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      email: z.string().email().optional(),
      phoneNumber: z.string().optional().nullable(),
    })
    .optional(),
  // Employee fields
  role: z.string().optional(),
  birthDate: z.string().optional().nullable(),
  gender: z
    .enum(['male', 'female', 'other', 'prefer_not_to_say'])
    .optional()
    .nullable(),
  avatarAttachmentId: z.number().optional().nullable(),
  defaultLatitude: z.string().optional().nullable(),
  defaultLongitude: z.string().optional().nullable(),
  defaultLocationAddress: z.string().optional().nullable(),
  commissionPercent: z.string().optional().nullable(),
  // Skills relation
  skills: z
    .array(
      z.object({
        skillId: z.number(),
        level: z.enum(['beginner', 'intermediate', 'expert']),
      }),
    )
    .optional(),
})

// Schema for creating employee with user info - nested structure for relation upsert
export const createEmployeeWithUserSchema = z.object({
  // User data is nested
  user: z.object({
    id: z.number().optional(),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email('Invalid email address').nullable(),
    phoneNumber: z.string().optional().nullable(),
  }),
  // Employee-specific fields
  role: z.string().min(1, 'Role is required'),
  birthDate: z.string().optional().nullable(), // Will be date string from form
  gender: z
    .enum(['male', 'female', 'other', 'prefer_not_to_say'])
    .optional()
    .nullable(),
  avatarAttachmentId: z.number().optional().nullable(),
  defaultLatitude: z.string().optional().nullable(),
  defaultLongitude: z.string().optional().nullable(),
  defaultLocationAddress: z.string().optional().nullable(),
  commissionPercent: z.string().optional().nullable(),
  // Skills relation
  skills: z
    .array(
      z.object({
        skillId: z.number(),
        level: z.enum(['beginner', 'intermediate', 'expert']),
      }),
    )
    .optional(),
})

// Schema for employee with user relation
export const selectEmployeeWithUserSchema = withAttachments(
  selectEmployeeSchema.extend({
    user: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string().nullable(),
        phoneNumber: z.string().nullable(),
      })
      .optional(),
    avatar: attachmentResponseSchema.partial().optional().nullable(),
    employeeSkills: z.array(selectEmployeeSkillWithDetailsSchema).optional(),
  }),
)

// Type exports
export type Employee = z.infer<typeof selectEmployeeSchema>
export type EmployeeWithUser = z.infer<typeof selectEmployeeWithUserSchema>
export type CreateEmployee = z.infer<typeof insertEmployeeSchema>
export type UpdateEmployee = z.infer<typeof updateEmployeeSchema>
export type CreateEmployeeWithUser = z.infer<
  typeof createEmployeeWithUserSchema
>
